import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Stage as KonvaStage } from "konva/lib/Stage";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, Navigate, useLocation, useNavigate, useParams } from "react-router";
import {
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  AlignStartVertical,
  AlignCenterVertical,
  AlignEndVertical,
  ArrowLeft,
  Bold,
  BringToFront,
  Eye,
  EyeOff,
  Frame,
  ImageIcon,
  Italic,
  Loader2,
  MoveDown,
  MoveUp,
  Save,
  SendToBack,
  Shapes,
  Square,
  Trash2,
  Type,
  UploadCloud,
  X,
  type LucideIcon,
} from "lucide-react";
import { toast } from "sonner";
import {
  applySocialPostTemplateEditBatch,
  createSocialPostEditableDraft,
  type SocialPostEditableDraft,
  type SocialPostImageRegenerationTaskRequestV1,
  type SocialPostFontStyle,
  type SocialPostSceneBlockV1,
  type SocialPostShapeType,
  type SocialPostTemplateEditOperation,
  type SocialPostTextAlign,
  type SocialPostVerticalAlign,
} from "@runcheetah/shared";

import { CampaignCreativeScenePreview } from "@/components/campaign/campaign-creative-scene-preview";
import { PostPreviewOverflowMenu } from "@/components/campaign/post-preview-overflow-menu";
import {
  CreativeLogoPicker,
  type CreativeLogoOption,
} from "@/components/campaign/creative-logo-picker";
import { CreativeEditorStudio } from "@/components/campaign/creative-editor-studio";
import {
  CreativeInspectorLabel as Label,
  CreativeInspectorNumberField as InspectorNumberField,
  CreativeInspectorSelectField as InspectorSelectField,
  CreativeInspectorSection as InspectorSection,
  CreativeInspectorSliderField as InspectorSliderField,
  CreativeLayerAddMenu as AddLayerMenu,
  CreativeLayerSortableRow,
  CreativeRectangleSizePresetControl as RectangleSizePresetControl,
} from "@/components/campaign/creative-layer-editor-controls";
import { CreativeLayersPanel } from "@/components/campaign/creative-layers-popover";
import {
  CreativeInspectorAdvancedGeometry,
  CreativeInspectorColorField,
  CreativeInspectorCornerRadiusField,
  CreativeInspectorFitField,
  CreativePostHashtagInput,
} from "@/components/campaign/creative-layer-inspector-fields";
import { RunConfirmationDialog } from "@/components/runs/run-confirmation-dialog";
import { useAssetCreationGate } from "@/hooks/use-asset-creation-gate";
import { downloadSocialPostPreviewImage } from "@/lib/social-post-preview-download";
import { normalizeSocialPostDraftLogoAssetKeys } from "@/lib/social-post-draft-logos";
import { mergeActiveSocialPostEditDraft } from "@/lib/social-post-editor-draft";
import { canPublishPostNow } from "@/lib/post-publish-now";
import { getCarouselArtboardSelectionBlockId } from "@/lib/carousel-editor-helpers";
import {
  createCreativeLayerCompactionUpdates,
  createCreativeLayerMoveEdgeUpdates,
  createCreativeLayerMoveStepUpdates,
  createCreativeLayerReorderUpdates,
  getSortedCreativeLayerEntries,
  getVisibleCreativeLayerEntries,
  type CreativeLayerEntry,
} from "@/components/campaign/campaign-creative-layer-ordering";
import {
  applyCreativeShapePreset,
  createDefaultCreativeLayerBlock,
  getNextRootLayerZIndex,
  type CreativeLayerCreateKind,
} from "@/components/campaign/campaign-creative-layer-factory";
import {
  canRemoveSelectedLayer,
  createBlockEditOperation,
  createLayerId,
  getCustomLayerName,
  truncateLayerLabel,
} from "@/components/campaign/creative-layer-editor-utils";
import { Button } from "@runcheetah/ui";
import { Textarea } from "@runcheetah/ui";
import {
  hasActiveImageRegenerationForPost,
  normalizeCampaignScheduledPosts,
} from "@/lib/campaign-posts";
import { type ImageFitValue } from "@/lib/image-fit-options";
import {
  EMPTY_POST_DRAFT_HISTORY,
  pushPostDraftHistory,
  redoPostDraftHistory,
  undoPostDraftHistory,
} from "@/lib/post-draft-history";
import { getPostDraftHydrationAction } from "@/lib/post-draft-sync";
import { formatInstagramProfileLabel } from "@/lib/social-post-chrome";
import { trpc } from "@/lib/trpc";
import { buildBrandPath, getUnavailableBrandPath, useRouteBrand } from "@/hooks/use-route-brand";
import { getCampaignPostDraftState } from "./campaign-post-state";
import { SocialPostSceneRenderer } from "@runcheetah/ui";

type CampaignPostPageMode = "page" | "focused";
type PartialImageRegenerationRequest = Extract<
  SocialPostImageRegenerationTaskRequestV1,
  { mode: "partial" }
>;

const BRAND_PALETTE_COLOR_RE = /^#[0-9a-f]{6}$/i;

function getBrandPrimaryPaletteColors(colors: string[] | null | undefined) {
  const seen = new Set<string>();
  const palette: string[] = [];

  for (const rawColor of colors ?? []) {
    const color = rawColor.trim().toUpperCase();
    const key = color.toLowerCase();

    if (!BRAND_PALETTE_COLOR_RE.test(color) || seen.has(key)) {
      continue;
    }

    seen.add(key);
    palette.push(color);
  }

  return palette;
}

function CampaignPostPage({ mode = "page" }: { mode?: CampaignPostPageMode } = {}) {
  const { id, postId } = useParams<{ id: string; postId: string }>();
  const intl = useIntl();
  const location = useLocation();
  const navigate = useNavigate();
  const [localDrafts, setLocalDrafts] = useState<Record<string, SocialPostEditableDraft>>({});
  const [downloadingImage, setDownloadingImage] = useState(false);
  const downloadStageRef = useRef<KonvaStage | null>(null);
  const [selectedCarouselSlideId, setSelectedCarouselSlideId] = useState<string | null>(null);
  const [carouselToolbarHost, setCarouselToolbarHost] = useState<HTMLDivElement | null>(null);
  const [carouselWorkspaceZoom, setCarouselWorkspaceZoom] = useState(1);
  const setDraftForPost = useCallback((scheduledPostId: string, draft: SocialPostEditableDraft) => {
    setLocalDrafts((current) => ({ ...current, [scheduledPostId]: draft }));
  }, []);
  const clearDraftForPost = useCallback((scheduledPostId: string) => {
    setLocalDrafts((current) => {
      const next = { ...current };
      delete next[scheduledPostId];
      return next;
    });
  }, []);

  const { brand, brandQuery } = useRouteBrand();
  const assetCreationGate = useAssetCreationGate(brand?.id);
  const isFocusedMode = mode === "focused";
  const hasBackgroundLocation = hasBackgroundLocationState(location.state);
  const instagramProfileLabel = formatInstagramProfileLabel(brand?.sourceDomain);

  const campaignQuery = trpc.campaign.get.useQuery(
    { brandId: brand?.id ?? "", id: id ?? "" },
    {
      enabled: Boolean(brand?.id && id),
      refetchOnWindowFocus: true,
      refetchInterval: (query) =>
        hasActiveImageRegenerationForPost(query.state.data?.scheduledPosts, postId) ? 2_000 : false,
    },
  );
  const campaign = campaignQuery.data ?? null;
  const utils = trpc.useUtils();
  const logosQuery = trpc.brand.listLogos.useQuery(
    { brandId: brand?.id ?? "" },
    { enabled: Boolean(brand?.id) },
  );
  const visualStyleQuery = trpc.brand.getVisualStyle.useQuery(
    { brandId: brand?.id ?? "" },
    { enabled: Boolean(brand?.id) },
  );
  const createLogoUpload = trpc.brand.createLogoUpload.useMutation();
  const completeLogoUpload = trpc.brand.completeLogoUpload.useMutation();
  const deleteLogo = trpc.brand.deleteLogo.useMutation();
  const saveDraftMutation = trpc.campaign.upsertPostDraft.useMutation({
    onSuccess: (data, variables) => {
      utils.campaign.get.setData(
        { brandId: variables.brandId, id: variables.campaignId },
        (current) => {
          if (!current) {
            return current;
          }

          return {
            ...current,
            scheduledPosts: current.scheduledPosts.map((scheduledPost) =>
              scheduledPost.id === variables.scheduledPostId
                ? {
                    ...scheduledPost,
                    draft: data.draft,
                    draftUpdatedAt: data.updatedAt,
                    hasUnpublishedDraft: data.draft
                      ? JSON.stringify(data.draft) !== JSON.stringify(scheduledPost.publishedDraft)
                      : false,
                  }
                : scheduledPost,
            ),
          };
        },
      );
      void Promise.all([
        utils.calendar.list.invalidate(),
        utils.brandAssistantChat.generatedAssets.invalidate(),
      ]);
    },
    onError: (_error, variables) => {
      void utils.campaign.get.invalidate({
        brandId: variables.brandId,
        id: variables.campaignId,
      });
    },
  });
  const publishDraftMutation = trpc.campaign.publishPostDraft.useMutation({
    onSuccess: async (data, variables) => {
      utils.campaign.get.setData(
        { brandId: variables.brandId, id: variables.campaignId },
        (current) => {
          if (!current) {
            return current;
          }

          return {
            ...current,
            scheduledPosts: current.scheduledPosts.map((scheduledPost) =>
              scheduledPost.id === variables.scheduledPostId
                ? {
                    ...scheduledPost,
                    draft: data.draft,
                    draftUpdatedAt: data.updatedAt,
                    publishedDraft: data.draft,
                    publishedAt: data.publishedAt,
                    hasUnpublishedDraft: data.hasUnpublishedDraft,
                  }
                : scheduledPost,
            ),
          };
        },
      );
      await Promise.all([
        utils.calendar.list.invalidate(),
        utils.brandAssistantChat.generatedAssets.invalidate(),
      ]);
    },
  });
  const publishNowMutation = trpc.campaign.publishNowInstagram.useMutation({
    onSuccess: async (_data, variables) => {
      await Promise.all([
        utils.campaign.get.invalidate({
          brandId: variables.brandId,
          id: variables.campaignId,
        }),
        utils.calendar.list.invalidate(),
        utils.brandAssistantChat.generatedAssets.invalidate(),
      ]);
      toast.success(
        intl.formatMessage({
          id: "campaignDetail.publishNowStarted",
          defaultMessage: "Instagram publishing started.",
        }),
      );
    },
  });
  const regenerateImageMutation = trpc.campaign.regeneratePostImage.useMutation({
    onSuccess: async (_data, variables) => {
      await utils.campaign.get.invalidate({
        brandId: variables.brandId,
        id: variables.request.campaignId ?? "",
      });
      await utils.brand.getBillingOverview.invalidate({ brandId: variables.brandId });
    },
  });

  const plannedPosts = useMemo(
    () => normalizeCampaignScheduledPosts(campaign?.scheduledPosts),
    [campaign?.scheduledPosts],
  );

  const post = plannedPosts.find((candidate) => candidate.id === postId) ?? null;
  const persistedDraft = post?.draft ?? null;
  const postDraft = post ? (localDrafts[post.id] ?? null) : null;
  const activeDraft = postDraft ?? persistedDraft;
  const persistedDraftKey = useMemo(() => JSON.stringify(persistedDraft), [persistedDraft]);
  const postDraftKey = useMemo(() => JSON.stringify(postDraft), [postDraft]);
  const hydratedPostIdRef = useRef<string | null>(null);
  const lastServerDraftKeyRef = useRef<string | null>(null);
  const [draftSyncWarning, setDraftSyncWarning] = useState<string | null>(null);
  const [draftHistory, setDraftHistory] = useState(EMPTY_POST_DRAFT_HISTORY);
  const [showLayersDrawer, setShowLayersDrawer] = useState(false);
  const [inspectorTab, setInspectorTab] = useState<"layers" | "caption">("layers");
  const [pendingImageRegenerationRequest, setPendingImageRegenerationRequest] =
    useState<PartialImageRegenerationRequest | null>(null);
  const imageRegenerationResolveRef = useRef<((accepted: boolean) => void) | null>(null);
  const imageRegenerationQuoteQuery = trpc.campaign.quoteRegeneratePostImage.useQuery(
    {
      brandId: brand?.id ?? "00000000-0000-0000-0000-000000000000",
      campaignId: campaign?.id ?? "00000000-0000-0000-0000-000000000000",
    },
    { enabled: Boolean(brand?.id && pendingImageRegenerationRequest), retry: false },
  );
  const brandPrimaryColors = useMemo(
    () => getBrandPrimaryPaletteColors(visualStyleQuery.data?.colorPalette),
    [visualStyleQuery.data?.colorPalette],
  );

  useEffect(() => {
    if (!post) {
      return;
    }

    const action = getPostDraftHydrationAction({
      postId: post.id,
      hydratedPostId: hydratedPostIdRef.current,
      lastServerDraftKey: lastServerDraftKeyRef.current,
      nextServerDraftKey: persistedDraftKey,
      currentClientDraftKey: postDraftKey,
    });

    if (action === "preserve_local") {
      lastServerDraftKeyRef.current = persistedDraftKey;
      setDraftSyncWarning(
        intl.formatMessage({
          id: "campaignPost.draftSyncWarning",
          defaultMessage: "A newer saved draft exists on the server. Your local edits were kept.",
        }),
      );
      return;
    }

    if (action === "acknowledge") {
      hydratedPostIdRef.current = post.id;
      lastServerDraftKeyRef.current = persistedDraftKey;
      setDraftSyncWarning(null);
      return;
    }

    if (action !== "hydrate") {
      return;
    }

    hydratedPostIdRef.current = post.id;
    lastServerDraftKeyRef.current = persistedDraftKey;
    setDraftSyncWarning(null);
    setDraftHistory(EMPTY_POST_DRAFT_HISTORY);

    if (persistedDraft) {
      setDraftForPost(post.id, persistedDraft);
    } else {
      clearDraftForPost(post.id);
    }
  }, [
    clearDraftForPost,
    intl,
    persistedDraft,
    persistedDraftKey,
    post,
    postDraftKey,
    setDraftForPost,
  ]);

  const longDateFmt = new Intl.DateTimeFormat(intl.locale, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

  const latestCompleted = post?.output.latestCompleted ?? null;
  const carouselSlides = useMemo(
    () => latestCompleted?.carousel?.slides ?? [],
    [latestCompleted?.carousel?.slides],
  );
  const isCarouselPost = carouselSlides.length > 0;
  const activeCarouselSlide =
    carouselSlides.find((slideOutput) => slideOutput.slide.id === selectedCarouselSlideId) ??
    carouselSlides[0] ??
    null;
  const activeCarouselSlideDraft = activeCarouselSlide
    ? (activeDraft?.carouselSlides.find(
        (slideDraft) => slideDraft.slideId === activeCarouselSlide.slide.id,
      ) ?? null)
    : null;
  const activePlan = isCarouselPost
    ? (activeCarouselSlideDraft?.plan ?? activeCarouselSlide?.scenePlan?.plan ?? null)
    : (activeDraft?.plan ?? post?.output.latestCompleted?.result?.plan ?? null);
  useEffect(() => {
    setSelectedCarouselSlideId(carouselSlides[0]?.slide.id ?? null);
  }, [carouselSlides, post?.id]);
  const canAddLogoLayer = !activePlan?.scene.blocks.some((block) => block.type === "logo");
  const generatedMetadata =
    post?.output.latestCopy?.result?.metadata ??
    post?.output.latestCompleted?.result?.metadata ??
    post?.output.latestAttempt?.result?.metadata ??
    null;
  const brandkit = latestCompleted?.preview?.brandkit ?? null;
  const logoOptions = useMemo(() => {
    const options: CreativeLogoOption[] = [];
    const uploadedLogos = logosQuery.data?.logos ?? [];
    if (uploadedLogos.length === 0 && brandkit?.logo) {
      options.push({
        id: `preview:${brandkit.logo.objectKey}`,
        objectKey: brandkit.logo.objectKey,
        signedUrl: brandkit.logo.signedUrl,
        label: intl.formatMessage({
          id: "campaignPost.logoPicker.currentLogo",
          defaultMessage: "Primary logo",
        }),
      });
    }

    for (const logo of uploadedLogos) {
      options.push({
        id: logo.id,
        objectKey: logo.objectKey,
        signedUrl: logo.signedUrl,
        label: logo.label,
        isPrimary: logo.isPrimary,
      });
    }

    return options;
  }, [brandkit?.logo, intl, logosQuery.data?.logos]);
  const logoAssets = useMemo(
    () =>
      (logosQuery.data?.logos ?? []).map((logo) => ({
        objectKey: logo.objectKey,
        signedUrl: logo.signedUrl,
        isPrimary: logo.isPrimary,
      })),
    [logosQuery.data?.logos],
  );
  const previewAsset = latestCompleted?.assets[0] ?? null;
  const latestCompletedAssets = activeCarouselSlide?.assets ?? latestCompleted?.assets ?? [];
  const blockCount = activePlan?.scene.blocks.length ?? 0;
  const longDateLabel = post ? longDateFmt.format(new Date(post.scheduledFor)) : "";
  const availableFontFamilies = useMemo(
    () => activePlan?.scene.fonts.map((font) => font.family) ?? [],
    [activePlan],
  );
  const publishedDraftKey = useMemo(
    () => JSON.stringify(post?.publishedDraft ?? null),
    [post?.publishedDraft],
  );
  const isPostEditable = post?.approvalStatus !== "approved";
  const hasPublishedDraft = Boolean(post?.publishedDraft);
  const hasPersistedDraft = Boolean(persistedDraft);
  const hasPostDraft = Boolean(postDraft);
  const draftState = useMemo(
    () =>
      getCampaignPostDraftState({
        postDraftKey,
        persistedDraftKey,
        publishedDraftKey,
        hasPublishedDraft,
        hasPersistedDraft,
        hasPostDraft,
        isPostEditable,
        isSavingDraft: saveDraftMutation.isPending,
        isPublishingDraft: publishDraftMutation.isPending,
      }),
    [
      hasPersistedDraft,
      hasPostDraft,
      hasPublishedDraft,
      isPostEditable,
      persistedDraftKey,
      postDraftKey,
      publishedDraftKey,
      publishDraftMutation.isPending,
      saveDraftMutation.isPending,
    ],
  );
  const { hasChangesToSave } = draftState;
  const canPublishNow = post
    ? canPublishPostNow({
        postStatus: post.status,
        approvalStatus: post.approvalStatus,
        postType: post.slot.postType,
        instagramConnected: post.instagramConnection?.isConnected === true,
        publication: post.publication,
        isPublishing: publishNowMutation.isPending,
      })
    : false;
  const shouldShowPublishNow = canPublishNow || publishNowMutation.isPending;
  const layerEntries = useMemo(() => getSortedCreativeLayerEntries(activePlan), [activePlan]);
  const editableBlocks = useMemo(() => layerEntries.map((entry) => entry.block), [layerEntries]);
  const layerDragSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(
    editableBlocks[0]?.id ?? null,
  );
  const [selectedBlockIds, setSelectedBlockIds] = useState<string[]>(
    editableBlocks[0]?.id ? [editableBlocks[0].id] : [],
  );
  const didClearSelectedBlockRef = useRef(false);
  const visibleLayerEntries = useMemo(
    () => getVisibleCreativeLayerEntries(layerEntries),
    [layerEntries],
  );
  const layerSortableIds = useMemo(
    () => visibleLayerEntries.map((entry) => entry.block.id),
    [visibleLayerEntries],
  );
  const selectedBlockExists = Boolean(
    selectedBlockId && editableBlocks.some((block) => block.id === selectedBlockId),
  );
  const selectedBlockIdsInScene = useMemo(
    () =>
      selectedBlockIds.filter((blockId) => editableBlocks.some((block) => block.id === blockId)),
    [editableBlocks, selectedBlockIds],
  );
  useEffect(() => {
    if (selectedBlockId === null) {
      if (didClearSelectedBlockRef.current || editableBlocks.length === 0) {
        return;
      }

      const nextBlockId = editableBlocks[0]?.id ?? null;
      setSelectedBlockId(nextBlockId);
      setSelectedBlockIds(nextBlockId ? [nextBlockId] : []);
      return;
    }

    if (selectedBlockId && editableBlocks.some((block) => block.id === selectedBlockId)) {
      didClearSelectedBlockRef.current = false;
      if (selectedBlockIds.length !== selectedBlockIdsInScene.length) {
        setSelectedBlockIds(selectedBlockIdsInScene);
      }
      return;
    }

    didClearSelectedBlockRef.current = false;
    const nextBlockId = editableBlocks[0]?.id ?? null;
    setSelectedBlockId(nextBlockId);
    setSelectedBlockIds(nextBlockId ? [nextBlockId] : []);
  }, [editableBlocks, selectedBlockId, selectedBlockIds.length, selectedBlockIdsInScene]);

  const selectLayer = (blockId: string, options?: { toggle?: boolean }) => {
    if (!blockId) {
      didClearSelectedBlockRef.current = true;
      setSelectedBlockId(null);
      setSelectedBlockIds([]);
      return;
    }

    didClearSelectedBlockRef.current = false;
    if (options?.toggle) {
      setSelectedBlockIds((current) => {
        const next = current.includes(blockId)
          ? current.filter((candidate) => candidate !== blockId)
          : [...current, blockId];
        setSelectedBlockId(next.at(-1) ?? null);
        didClearSelectedBlockRef.current = next.length === 0;
        return next;
      });
      return;
    }

    setSelectedBlockId(blockId);
    setSelectedBlockIds([blockId]);
  };

  const selectLayers = (blockIds: string[]) => {
    const validBlockIds = blockIds.filter((blockId) =>
      editableBlocks.some((block) => block.id === blockId),
    );

    didClearSelectedBlockRef.current = validBlockIds.length === 0;
    setSelectedBlockId(validBlockIds.at(-1) ?? null);
    setSelectedBlockIds(validBlockIds);
  };

  const selectedBlock = editableBlocks.find((block) => block.id === selectedBlockId) ?? null;
  const selectedTransformBlockId = selectedBlockExists ? selectedBlockId : null;
  const selectedTransformBlockIds = selectedBlockIdsInScene;
  const ensureDraft = () => {
    if (postDraft) {
      return postDraft;
    }

    if (persistedDraft) {
      return persistedDraft;
    }

    if (!activePlan) {
      return null;
    }

    return createSocialPostEditableDraft({
      plan: activePlan,
      caption: generatedMetadata?.caption ?? null,
      hashtags: generatedMetadata?.hashtags ?? [],
      carouselSlides: carouselSlides.flatMap((slideOutput) => {
        const plan =
          activeDraft?.carouselSlides.find(
            (slideDraft) => slideDraft.slideId === slideOutput.slide.id,
          )?.plan ??
          slideOutput.scenePlan?.plan ??
          null;

        return plan
          ? [
              {
                slideId: slideOutput.slide.id,
                slideIndex: slideOutput.slide.index,
                plan,
                imageAssetOverrides: [],
              },
            ]
          : [];
      }),
    });
  };

  const applyOperations = (summary: string, operations: SocialPostTemplateEditOperation[]) => {
    if (!isPostEditable) {
      return;
    }

    const baseDraft = ensureDraft();
    if (!baseDraft || !post || !activePlan) {
      return;
    }

    const nextActiveDraft = applySocialPostTemplateEditBatch(
      {
        ...baseDraft,
        plan: activePlan,
        imageAssetOverrides: activeCarouselSlideDraft?.imageAssetOverrides ?? [],
      },
      { summary, operations },
    );
    const nextDraft = mergeActiveSocialPostEditDraft({
      baseDraft,
      activeDraft: nextActiveDraft,
      carouselTarget: activeCarouselSlide
        ? {
            slideId: activeCarouselSlide.slide.id,
            slideIndex: activeCarouselSlide.slide.index,
          }
        : null,
    });
    setDraftHistory((history) =>
      pushPostDraftHistory({ history, current: baseDraft, next: nextDraft }),
    );
    setDraftForPost(post.id, nextDraft);
  };

  const addLayer = (kind: CreativeLayerCreateKind) => {
    if (!isPostEditable || !activePlan) {
      return;
    }
    if (kind === "logo" && !canAddLogoLayer) {
      return;
    }

    const defaultLogoObjectKey =
      logoOptions.find((logo) => logo.isPrimary)?.objectKey ?? logoOptions[0]?.objectKey ?? null;

    const block = createDefaultCreativeLayerBlock({
      kind,
      id: createLayerId(kind),
      canvas: activePlan.scene.canvas,
      zIndex: getNextRootLayerZIndex(activePlan.scene.blocks),
      fontFamily: availableFontFamilies[0],
      logoObjectKey: defaultLogoObjectKey,
    });

    applyOperations(`Add ${kind} layer`, [{ type: "add_block", block }]);
    didClearSelectedBlockRef.current = false;
    setSelectedBlockId(block.id);
    setSelectedBlockIds([block.id]);
  };

  const removeSelectedLayer = () => {
    if (!isPostEditable || !activePlan || selectedBlockIdsInScene.length === 0) {
      return;
    }

    const removableBlockIds = selectedBlockIdsInScene.slice(
      0,
      Math.max(0, activePlan.scene.blocks.length - 1),
    );
    if (removableBlockIds.length === 0) {
      return;
    }

    const removableBlockIdSet = new Set(removableBlockIds);
    const remainingBlocks = activePlan.scene.blocks.filter(
      (block) => !removableBlockIdSet.has(block.id),
    );
    const compactionOperations = createCreativeLayerCompactionUpdates(remainingBlocks).flatMap(
      (update) => {
        const block = remainingBlocks.find((candidate) => candidate.id === update.blockId);
        const operation = block ? createBlockEditOperation(block, update.changes) : null;
        return operation ? [operation] : [];
      },
    );

    applyOperations("Remove selected layer", [
      ...removableBlockIds.map((blockId) => ({ type: "remove_block" as const, blockId })),
      ...compactionOperations,
    ]);
    didClearSelectedBlockRef.current = true;
    setSelectedBlockId(null);
    setSelectedBlockIds([]);
  };

  const updateSelectedBlock = (summary: string, changes: Record<string, unknown>) => {
    if (!selectedBlock) {
      return;
    }

    const operation = createBlockEditOperation(selectedBlock, changes);
    if (!operation) {
      return;
    }

    applyOperations(summary, [operation]);
  };

  const uploadLogoForSelectedBlock = async (file: File) => {
    if (!brand?.id || selectedBlock?.type !== "logo") {
      return;
    }

    const upload = await createLogoUpload.mutateAsync({
      brandId: brand.id,
      fileName: file.name,
      contentType: file.type as "image/png" | "image/jpeg" | "image/webp" | "image/svg+xml",
      sizeBytes: file.size,
    });
    const response = await fetch(upload.uploadUrl, {
      method: "PUT",
      headers: { "Content-Type": upload.contentType },
      body: file,
    });

    if (!response.ok) {
      throw new Error(`Logo upload failed with status ${response.status}`);
    }

    const result = await completeLogoUpload.mutateAsync({
      brandId: brand.id,
      objectKey: upload.objectKey,
      fileName: file.name,
      contentType: upload.contentType,
      sizeBytes: file.size,
      makePrimary: false,
      regenerateVisualIdentity: false,
    });
    await utils.brand.listLogos.invalidate({ brandId: brand.id });
    updateSelectedBlock("Change logo", { assetObjectKey: result.logo.objectKey });
  };

  const deleteLogoFromPicker = async (logo: CreativeLogoOption) => {
    if (!brand?.id || logo.isPrimary || logo.id.startsWith("preview:")) {
      return;
    }

    await deleteLogo.mutateAsync({ brandId: brand.id, logoId: logo.id });
    await utils.brand.listLogos.invalidate({ brandId: brand.id });

    const primaryLogo = (logosQuery.data?.logos ?? []).find((candidate) => candidate.isPrimary);
    if (
      selectedBlock?.type === "logo" &&
      selectedBlock.assetObjectKey === logo.objectKey &&
      primaryLogo
    ) {
      updateSelectedBlock("Change logo", { assetObjectKey: primaryLogo.objectKey });
    }
  };

  const getCurrentCaption = () => activeDraft?.caption ?? generatedMetadata?.caption ?? "";
  const getCurrentHashtags = () => activeDraft?.hashtags ?? generatedMetadata?.hashtags ?? [];

  const updatePostMetadata = (caption: string, hashtags = getCurrentHashtags()) => {
    applyOperations("Update post metadata draft", [{ type: "update_caption", caption, hashtags }]);
  };

  const updateCaption = (caption: string) => {
    updatePostMetadata(caption);
  };

  const updateHashtags = (hashtags: string[]) => {
    updatePostMetadata(getCurrentCaption(), hashtags);
  };

  const updateBlockById = (blockId: string, changes: Record<string, unknown>) => {
    const targetBlock = editableBlocks.find((block) => block.id === blockId);
    if (!targetBlock) {
      return;
    }

    const operation = createBlockEditOperation(targetBlock, changes);
    if (!operation) {
      return;
    }

    applyOperations("Update layer from canvas", [operation]);
  };

  const updateBlocksById = (
    updates: Array<{ blockId: string; changes: Record<string, unknown> }>,
  ) => {
    const operations = updates.flatMap((update) => {
      const targetBlock = editableBlocks.find((block) => block.id === update.blockId);

      if (!targetBlock) {
        return [];
      }

      const operation = createBlockEditOperation(targetBlock, update.changes);
      return operation ? [operation] : [];
    });

    if (operations.length === 0) {
      return;
    }

    applyOperations("Update layers from canvas", operations);
  };

  const submitImageRegeneration = async (request: {
    blockId: string;
    sourceGeneratedAssetId: string;
    prompt?: string;
    brushRadius: number;
    strokes: Array<{ id: string; points: Array<{ x: number; y: number }> }>;
  }) => {
    if (!brand?.id || !campaign?.id || !post) {
      return false;
    }

    if (imageRegenerationResolveRef.current) {
      return false;
    }

    const regenerationRequest: PartialImageRegenerationRequest = {
      type: "social_post_image_regeneration.request",
      version: 1,
      mode: "partial",
      campaignId: campaign.id,
      scheduledPostId: post.id,
      applyAsDraftOverride: true,
      ...request,
    };

    setPendingImageRegenerationRequest(regenerationRequest);

    return await new Promise<boolean>((resolve) => {
      imageRegenerationResolveRef.current = resolve;
    });
  };

  const confirmImageRegeneration = async () => {
    if (!brand?.id || !pendingImageRegenerationRequest) {
      return;
    }

    try {
      await regenerateImageMutation.mutateAsync({
        brandId: brand.id,
        request: pendingImageRegenerationRequest,
      });
      imageRegenerationResolveRef.current?.(true);
      imageRegenerationResolveRef.current = null;
      setPendingImageRegenerationRequest(null);
    } catch {
      // Keep the dialog open so the API error can be shown and retried.
    }
  };

  const cancelImageRegeneration = () => {
    if (regenerateImageMutation.isPending) {
      return;
    }

    imageRegenerationResolveRef.current?.(false);
    imageRegenerationResolveRef.current = null;
    setPendingImageRegenerationRequest(null);
  };

  const reorderLayer = (event: DragEndEvent) => {
    const overId = event.over?.id;
    const activeId = event.active.id;

    if (!activePlan || !overId || activeId === overId) {
      return;
    }

    const updates = createCreativeLayerReorderUpdates({
      blocks: activePlan.scene.blocks,
      activeBlockId: String(activeId),
      overBlockId: String(overId),
    });

    if (updates.length === 0) {
      return;
    }

    updateBlocksById(updates);
  };

  const moveLayerForward = () => {
    if (!activePlan || !selectedBlockId) return;
    updateBlocksById(
      createCreativeLayerMoveStepUpdates({
        blocks: activePlan.scene.blocks,
        blockId: selectedBlockId,
        direction: "forward",
      }),
    );
  };

  const moveLayerBackward = () => {
    if (!activePlan || !selectedBlockId) return;
    updateBlocksById(
      createCreativeLayerMoveStepUpdates({
        blocks: activePlan.scene.blocks,
        blockId: selectedBlockId,
        direction: "backward",
      }),
    );
  };

  const moveLayerToFront = () => {
    if (!activePlan || !selectedBlockId) return;
    updateBlocksById(
      createCreativeLayerMoveEdgeUpdates({
        blocks: activePlan.scene.blocks,
        blockId: selectedBlockId,
        edge: "front",
      }),
    );
  };

  const moveLayerToBack = () => {
    if (!activePlan || !selectedBlockId) return;
    updateBlocksById(
      createCreativeLayerMoveEdgeUpdates({
        blocks: activePlan.scene.blocks,
        blockId: selectedBlockId,
        edge: "back",
      }),
    );
  };

  const saveCurrentChanges = async () => {
    if (!isPostEditable || !brand?.id || !campaign?.id || !post || !postDraft) {
      return;
    }

    const draftToSave = normalizeSocialPostDraftLogoAssetKeys({
      draft: postDraft,
      logoAssets: logosQuery.data?.logos ?? [],
    });
    if (!draftToSave) {
      return;
    }

    try {
      const saved = await saveDraftMutation.mutateAsync({
        brandId: brand.id,
        campaignId: campaign.id,
        scheduledPostId: post.id,
        draft: draftToSave,
      });
      await publishDraftMutation.mutateAsync({
        brandId: brand.id,
        campaignId: campaign.id,
        scheduledPostId: post.id,
        expectedDraftUpdatedAt: saved.updatedAt,
      });
    } catch {
      // Mutation errors are rendered in the editor action area.
    }
  };

  const publishNow = () => {
    if (!canPublishNow || !brand?.id || !campaign?.id || !post) {
      return;
    }

    publishNowMutation.mutate({
      brandId: brand.id,
      campaignId: campaign.id,
      scheduledPostId: post.id,
    });
  };

  const undoDraftChange = () => {
    if (!isPostEditable) {
      return;
    }

    if (!post) {
      return;
    }

    const result = undoPostDraftHistory({ history: draftHistory, current: postDraft });
    setDraftHistory(result.history);

    if (result.draft) {
      setDraftForPost(post.id, result.draft);
    } else {
      clearDraftForPost(post.id);
    }
  };

  const redoDraftChange = () => {
    if (!isPostEditable) {
      return;
    }

    if (!post) {
      return;
    }

    const result = redoPostDraftHistory({ history: draftHistory, current: postDraft });
    setDraftHistory(result.history);

    if (result.draft) {
      setDraftForPost(post.id, result.draft);
    } else {
      clearDraftForPost(post.id);
    }
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (
        target?.tagName === "INPUT" ||
        target?.tagName === "TEXTAREA" ||
        target?.tagName === "SELECT" ||
        target?.tagName === "BUTTON" ||
        target?.isContentEditable
      ) {
        return;
      }

      const isUndoRedoShortcut = event.metaKey || event.ctrlKey;
      const key = event.key.toLowerCase();

      if (!isUndoRedoShortcut && !event.altKey && (key === "backspace" || key === "delete")) {
        if (
          !event.repeat &&
          selectedBlockIdsInScene.length > 0 &&
          activePlan &&
          activePlan.scene.blocks.length > 1
        ) {
          event.preventDefault();
          removeSelectedLayer();
        }
        return;
      }

      if (!isUndoRedoShortcut) {
        return;
      }

      if (key === "z" && event.shiftKey) {
        event.preventDefault();
        redoDraftChange();
        return;
      }

      if (key === "z") {
        event.preventDefault();
        undoDraftChange();
        return;
      }

      if (key === "y") {
        event.preventDefault();
        redoDraftChange();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  });

  if (brandQuery.isPending || campaignQuery.isPending) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-hidden="true" />
      </div>
    );
  }

  if (!brand || brand.status !== "scanned") {
    return <Navigate to={getUnavailableBrandPath(brand?.id)} replace />;
  }

  if (!campaign || !post) {
    return (
      <Navigate to={brand ? buildBrandPath(brand.id, `/campaigns/${id ?? ""}`) : "/app"} replace />
    );
  }

  const handleReturn = () => {
    if (isFocusedMode && hasBackgroundLocation) {
      navigate(-1);
      return;
    }

    navigate(buildBrandPath(brand.id, `/campaigns/${campaign.id}`));
  };

  const downloadImage = async () => {
    setDownloadingImage(true);

    try {
      await downloadSocialPostPreviewImage({
        stage: downloadStageRef.current,
        previewAssetUrl: previewAsset?.signedUrl ?? null,
        fileName: `campaign-${post.id}.png`,
      });
    } catch (caught) {
      toast.error(
        caught instanceof Error
          ? caught.message
          : intl.formatMessage({
              id: "approvals.downloadFailed",
              defaultMessage: "Could not download the image.",
            }),
      );
    } finally {
      setDownloadingImage(false);
    }
  };

  return (
    <>
      {activePlan ? (
        <div className="pointer-events-none fixed -left-[10000px] top-0 opacity-0" aria-hidden>
          <SocialPostSceneRenderer
            plan={activePlan}
            linkedAssets={latestCompletedAssets}
            brandkit={brandkit}
            logoAssets={logoAssets}
            stageRef={downloadStageRef}
          />
        </div>
      ) : null}
      <CreativeEditorStudio
        className={isFocusedMode ? "fixed inset-0 z-20" : "h-full w-full"}
        eyebrow={
          <FormattedMessage id="campaignPost.editorEyebrow" defaultMessage="Campaign creative" />
        }
        title={post.slot.concept}
        meta={longDateLabel}
        status={
          saveDraftMutation.isPending || publishDraftMutation.isPending
            ? intl.formatMessage({ id: "common.saving", defaultMessage: "Saving…" })
            : hasChangesToSave
              ? intl.formatMessage({
                  id: "campaignPost.draftStatus.unsavedChanges",
                  defaultMessage: "Unsaved changes",
                })
              : null
        }
        backAction={
          isFocusedMode ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="-ml-3 w-fit gap-2 rounded-full text-foreground/62 hover:bg-foreground/6 hover:text-foreground"
              onClick={handleReturn}
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              <FormattedMessage
                id={hasBackgroundLocation ? "common.back" : "campaignPost.backToCampaign"}
                defaultMessage={hasBackgroundLocation ? "Back" : "Back to campaign"}
              />
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              asChild
              className="-ml-3 w-fit gap-2 rounded-full text-foreground/62 hover:bg-foreground/6 hover:text-foreground"
            >
              <Link to={buildBrandPath(brand.id, `/campaigns/${campaign.id}`)}>
                <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                <FormattedMessage
                  id="campaignPost.backToCampaign"
                  defaultMessage="Back to campaign"
                />
              </Link>
            </Button>
          )
        }
        actions={
          <>
            <PostPreviewOverflowMenu
              downloading={downloadingImage}
              onDownload={() => void downloadImage()}
            />
            <Button
              type="button"
              size="sm"
              className="rounded-full bg-foreground text-primary-foreground shadow-[0_10px_30px_rgba(19,34,49,0.16)] hover:bg-foreground/90"
              onClick={() => void saveCurrentChanges()}
              disabled={!draftState.canSaveChanges}
            >
              {saveDraftMutation.isPending || publishDraftMutation.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
              ) : (
                <Save className="h-3.5 w-3.5" aria-hidden="true" />
              )}
              <FormattedMessage id="common.save" defaultMessage="Save" />
            </Button>
            {shouldShowPublishNow ? (
              <Button
                type="button"
                size="sm"
                variant="brand"
                className="rounded-full"
                disabled={!canPublishNow}
                onClick={publishNow}
              >
                {publishNowMutation.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                ) : (
                  <UploadCloud className="h-3.5 w-3.5" aria-hidden="true" />
                )}
                <FormattedMessage
                  id="standalonePost.actions.publishNow"
                  defaultMessage="Publish now"
                />
              </Button>
            ) : null}
          </>
        }
        warnings={
          !isPostEditable ? (
            <FormattedMessage
              id="campaignPost.editLockedApproved"
              defaultMessage="This post is approved and can no longer be edited. Create a new draft or unapprove it before making changes."
            />
          ) : (
            draftSyncWarning
          )
        }
        errors={
          saveDraftMutation.error || publishDraftMutation.error || publishNowMutation.error
            ? (saveDraftMutation.error?.message ??
              publishDraftMutation.error?.message ??
              publishNowMutation.error?.message ??
              intl.formatMessage({
                id: "campaignPost.draftActionFailed",
                defaultMessage: "Draft action failed.",
              }))
            : null
        }
        canvas={
          activePlan ? (
            isCarouselPost ? (
              <div className="relative min-h-0 flex-1 overflow-hidden rounded-[2rem] border border-border/70 bg-[#fffdf8] shadow-[0_26px_70px_rgba(19,34,49,0.14)]">
                <div
                  ref={setCarouselToolbarHost}
                  className="pointer-events-none absolute inset-0 z-50"
                />
                <div
                  className="pointer-events-none absolute inset-0 opacity-95"
                  style={{
                    backgroundImage:
                      "radial-gradient(circle at 50% 14%, rgba(255,241,212,0.38), transparent 24rem), radial-gradient(circle at 18% 84%, rgba(255,241,212,0.42), transparent 18rem), linear-gradient(rgba(19,34,49,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(19,34,49,0.04) 1px, transparent 1px), radial-gradient(circle at 1px 1px, rgba(19,34,49,0.09) 1px, transparent 0)",
                    backgroundPosition: "-1px -1px, -1px -1px, 0 0",
                    backgroundSize: "auto, auto, 96px 96px, 96px 96px, 12px 12px",
                  }}
                  aria-hidden="true"
                />
                <div className="relative h-full min-h-[34rem] overflow-auto px-6 py-8">
                  <div className="flex min-w-max items-start gap-12 pr-6">
                    {carouselSlides.map((slideOutput) => {
                      const slideDraft = activeDraft?.carouselSlides.find(
                        (candidate) => candidate.slideId === slideOutput.slide.id,
                      );
                      const slidePlan = slideDraft?.plan ?? slideOutput.scenePlan?.plan ?? null;
                      const isActiveSlide = activeCarouselSlide?.slide.id === slideOutput.slide.id;

                      if (!slidePlan) {
                        return null;
                      }

                      return (
                        <div
                          key={slideOutput.slide.id}
                          className={[
                            "relative flex shrink-0 flex-col gap-3",
                            isActiveSlide ? "z-20" : "z-0",
                          ].join(" ")}
                          style={{
                            width: `min(${Math.round(480 * carouselWorkspaceZoom)}px, calc(100vw - 4rem))`,
                          }}
                        >
                          <button
                            type="button"
                            className={[
                              "flex items-center justify-between rounded-2xl border px-3 py-2 text-left text-xs transition-colors",
                              isActiveSlide
                                ? "border-brand/55 bg-brand/12 text-foreground shadow-[0_10px_30px_rgba(19,34,49,0.08)]"
                                : "border-border/60 bg-background/62 text-foreground/55 hover:border-foreground/20 hover:bg-background/78 hover:text-foreground",
                            ].join(" ")}
                            onClick={() => {
                              setSelectedCarouselSlideId(slideOutput.slide.id);
                              const nextBlockId = getCarouselArtboardSelectionBlockId({
                                currentBlockId: selectedBlockId,
                                slideBlocks: slidePlan.scene.blocks,
                              });
                              if (nextBlockId !== selectedBlockId) {
                                setSelectedBlockId(nextBlockId);
                                setSelectedBlockIds(nextBlockId ? [nextBlockId] : []);
                              }
                            }}
                          >
                            <span className="font-semibold uppercase tracking-[0.16em]">
                              <FormattedMessage
                                id="campaignPost.carouselSlideLabel"
                                defaultMessage="Slide {index}"
                                values={{ index: slideOutput.slide.index }}
                              />
                            </span>
                            <span className="truncate pl-3 font-medium capitalize">
                              {slideOutput.slide.role}
                            </span>
                          </button>
                          <div
                            className={[
                              "rounded-[1.6rem] border bg-background/74 p-2 transition-[border-color,box-shadow,background-color]",
                              isActiveSlide
                                ? "border-brand/55 shadow-[0_18px_54px_rgba(19,34,49,0.16)]"
                                : "border-border/65 shadow-[0_14px_42px_rgba(19,34,49,0.08)]",
                            ].join(" ")}
                          >
                            <CampaignCreativeScenePreview
                              plan={slidePlan}
                              linkedAssets={slideOutput.assets}
                              brandkit={brandkit}
                              logoAssets={logoAssets}
                              profileLabel={instagramProfileLabel}
                              caption={getCurrentCaption()}
                              hashtags={getCurrentHashtags()}
                              maxWidth={448 * carouselWorkspaceZoom}
                              selectedBlockId={isActiveSlide ? selectedBlockId : null}
                              selectedBlockIds={isActiveSlide ? selectedBlockIdsInScene : []}
                              selectedTransformBlockId={
                                isActiveSlide ? selectedTransformBlockId : null
                              }
                              selectedTransformBlockIds={
                                isActiveSlide ? selectedTransformBlockIds : []
                              }
                              showWorkspaceControls={isActiveSlide}
                              workspaceControlsPortal={carouselToolbarHost}
                              workspaceZoomLabel={`${Math.round(carouselWorkspaceZoom * 100)}%`}
                              onWorkspaceZoomOut={() =>
                                setCarouselWorkspaceZoom((current) => Math.max(0.6, current * 0.85))
                              }
                              onWorkspaceFitView={() => setCarouselWorkspaceZoom(1)}
                              onWorkspaceZoomIn={() =>
                                setCarouselWorkspaceZoom((current) => Math.min(2, current * 1.15))
                              }
                              onWorkspaceWheelZoom={(factor) =>
                                setCarouselWorkspaceZoom((current) =>
                                  Math.min(2, Math.max(0.6, current * factor)),
                                )
                              }
                              flat={true}
                              artboard={true}
                              onCaptionDoubleClick={() => {
                                setSelectedCarouselSlideId(slideOutput.slide.id);
                                setInspectorTab("caption");
                              }}
                              onSelectBlock={(blockId, options) => {
                                setSelectedCarouselSlideId(slideOutput.slide.id);
                                selectLayer(blockId, options);
                                setInspectorTab("layers");
                              }}
                              onSelectBlocks={(blockIds) => {
                                setSelectedCarouselSlideId(slideOutput.slide.id);
                                selectLayers(blockIds);
                                setInspectorTab("layers");
                              }}
                              onUpdateBlock={(blockId, changes) => {
                                setSelectedCarouselSlideId(slideOutput.slide.id);
                                updateBlockById(blockId, changes);
                              }}
                              onUpdateBlocks={(updates) => {
                                setSelectedCarouselSlideId(slideOutput.slide.id);
                                updateBlocksById(updates);
                              }}
                              isImageRegenerationPending={regenerateImageMutation.isPending}
                              activeImageRegenerationBlockIds={post.activeImageRegenerationBlockIds}
                              imageRegenerationError={
                                regenerateImageMutation.error?.message ?? null
                              }
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex min-h-0 flex-1 flex-col">
                <CampaignCreativeScenePreview
                  plan={activePlan}
                  linkedAssets={post.output.latestCompleted?.assets ?? []}
                  brandkit={brandkit}
                  logoAssets={logoAssets}
                  profileLabel={instagramProfileLabel}
                  caption={getCurrentCaption()}
                  hashtags={getCurrentHashtags()}
                  selectedBlockId={selectedBlockId}
                  selectedBlockIds={selectedBlockIdsInScene}
                  selectedTransformBlockId={selectedTransformBlockId}
                  selectedTransformBlockIds={selectedTransformBlockIds}
                  canUndo={draftHistory.past.length > 0}
                  canRedo={draftHistory.future.length > 0}
                  onUndo={undoDraftChange}
                  onRedo={redoDraftChange}
                  onSelectBlock={(blockId, options) => {
                    selectLayer(blockId, options);
                    setInspectorTab("layers");
                  }}
                  onSelectBlocks={(blockIds) => {
                    selectLayers(blockIds);
                    setInspectorTab("layers");
                  }}
                  onUpdateBlock={updateBlockById}
                  onUpdateBlocks={updateBlocksById}
                  onCaptionDoubleClick={() => setInspectorTab("caption")}
                  onSubmitImageRegeneration={submitImageRegeneration}
                  isImageRegenerationPending={regenerateImageMutation.isPending}
                  activeImageRegenerationBlockIds={post.activeImageRegenerationBlockIds}
                  imageRegenerationError={regenerateImageMutation.error?.message ?? null}
                />
              </div>
            )
          ) : previewAsset ? (
            <div className="mx-auto max-w-[30rem] xl:mx-0 xl:pt-1">
              <img
                src={previewAsset.signedUrl}
                alt={intl.formatMessage(
                  {
                    id: "campaignPost.creativeAlt",
                    defaultMessage: "Generated campaign creative for {concept}",
                  },
                  { concept: post.slot.concept },
                )}
                width={previewAsset.width ?? 1080}
                height={previewAsset.height ?? 1350}
                className="mx-auto block h-auto w-full object-contain"
              />
            </div>
          ) : (
            <div className="flex min-h-[18rem] w-full flex-col items-center justify-center gap-3 rounded-3xl border border-dashed border-border/70 bg-background/60 px-6 text-center">
              <ImageIcon className="h-10 w-10 text-foreground/35" aria-hidden="true" />
              <div className="space-y-1">
                <p className="font-medium text-foreground">
                  <FormattedMessage
                    id="campaignPost.creativePreviewUnavailable"
                    defaultMessage="Creative preview unavailable"
                  />
                </p>
                <p className="max-w-md text-sm leading-6 text-foreground/60">
                  <FormattedMessage
                    id="campaignPost.creativePreviewUnavailableDescription"
                    defaultMessage="We'll show the draft or generated creative here once a template is available for this post."
                  />
                </p>
              </div>
            </div>
          )
        }
        inspector={
          <>
            <div className="mb-4 rounded-3xl border border-border/70 bg-background/80 p-3 shadow-[0_12px_30px_rgba(19,34,49,0.06)]">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  {showLayersDrawer ? (
                    <BringToFront className="h-4 w-4 text-foreground/65" aria-hidden="true" />
                  ) : inspectorTab === "caption" ? (
                    <Type className="h-4 w-4 text-foreground/65" aria-hidden="true" />
                  ) : (
                    (() => {
                      const iconClass = "h-4 w-4 text-foreground/65";
                      switch (selectedBlock?.type) {
                        case "text":
                          return <Type className={iconClass} aria-hidden="true" />;
                        case "image":
                          return <ImageIcon className={iconClass} aria-hidden="true" />;
                        case "logo":
                          return <ImageIcon className={iconClass} aria-hidden="true" />;
                        case "rect":
                          return <Square className={iconClass} aria-hidden="true" />;
                        case "shape":
                          return <Shapes className={iconClass} aria-hidden="true" />;
                        case "frame":
                          return <Frame className={iconClass} aria-hidden="true" />;
                        default:
                          return null;
                      }
                    })()
                  )}
                  <span className="font-display text-base font-semibold tracking-[-0.02em] text-foreground">
                    {showLayersDrawer
                      ? "Layers"
                      : inspectorTab === "caption"
                        ? "Caption"
                        : (() => {
                            switch (selectedBlock?.type) {
                              case "text":
                                return "Text";
                              case "image":
                                return "Image";
                              case "logo":
                                return "Logo";
                              case "rect":
                                return "Rectangle";
                              case "shape":
                                return "Shape";
                              case "frame":
                                return "Frame";
                              default:
                                return "Canvas";
                            }
                          })()}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <AddLayerMenu
                    compact
                    align="right"
                    disabled={!isPostEditable || !activePlan}
                    label={intl.formatMessage({
                      id: "campaignPost.addLayer",
                      defaultMessage: "Add layer",
                    })}
                    items={[
                      { label: "Text", icon: Type, onClick: () => addLayer("text") },
                      ...(canAddLogoLayer
                        ? [{ label: "Logo", icon: ImageIcon, onClick: () => addLayer("logo") }]
                        : []),
                      {
                        label: "Shapes",
                        icon: Shapes,
                        children: [
                          { label: "Rectangle", icon: Square, onClick: () => addLayer("rect") },
                          {
                            label: "Ellipse / line",
                            icon: Shapes,
                            onClick: () => addLayer("shape"),
                          },
                        ],
                      },
                      { label: "Frame", icon: Frame, onClick: () => addLayer("frame") },
                    ]}
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant={showLayersDrawer ? "secondary" : "outline"}
                    className="h-8 w-8 rounded-full p-0"
                    onClick={() => setShowLayersDrawer((current) => !current)}
                    aria-label={intl.formatMessage({
                      id: "campaignPost.layersDrawerToggle",
                      defaultMessage: "Layers",
                    })}
                  >
                    <BringToFront className="h-3.5 w-3.5" aria-hidden="true" />
                  </Button>
                </div>
              </div>
              <p className="mt-2 text-xs leading-5 text-foreground/48">
                {showLayersDrawer
                  ? "Reorder, hide, or select layers in this post."
                  : inspectorTab === "caption"
                    ? "Refine the post copy and hashtag set."
                    : selectedBlock
                      ? "Tune the selected layer with precise creative controls."
                      : "Select a layer on the canvas or open Layers to start editing."}
              </p>
            </div>

            {showLayersDrawer ? (
              <CreativeLayersPanel
                title={<FormattedMessage id="campaignPost.layers" defaultMessage="Layers" />}
                count={blockCount}
                actions={
                  <div className="flex flex-wrap items-center justify-end gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="justify-center rounded-2xl border-destructive/20 bg-destructive/6 text-destructive hover:bg-destructive/10 disabled:border-border/60 disabled:bg-muted/35 disabled:text-foreground/32"
                      disabled={
                        selectedBlockIdsInScene.length === 0 ||
                        (activePlan?.scene.blocks.length ?? 0) <= 1
                      }
                      onClick={removeSelectedLayer}
                    >
                      <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                      <FormattedMessage
                        id="campaignPost.deleteLayer"
                        defaultMessage="Delete layer"
                      />
                    </Button>
                  </div>
                }
              >
                <DndContext
                  sensors={layerDragSensors}
                  collisionDetection={closestCenter}
                  autoScroll={false}
                  onDragEnd={reorderLayer}
                >
                  <SortableContext items={layerSortableIds} strategy={verticalListSortingStrategy}>
                    <div className="grid gap-1.5">
                      {visibleLayerEntries.map((entry) => (
                        <SortableLayerRow
                          key={entry.block.id}
                          entry={entry}
                          isActive={selectedBlockIdsInScene.includes(entry.block.id)}
                          isTransformActive={false}
                          onSelectBlock={selectLayer}
                          onRenameBlock={(blockId, layerName) =>
                            updateBlockById(blockId, { layerName })
                          }
                          onToggleVisibility={(blockId, visible) =>
                            updateBlockById(blockId, { visible })
                          }
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              </CreativeLayersPanel>
            ) : (
              <div className="min-w-0 motion-safe:animate-[inspector-fade-in_0.1s_ease-out]">
                {inspectorTab === "caption" ? (
                  <div className="space-y-2.5">
                    <Textarea
                      autoFocus
                      value={getCurrentCaption()}
                      onChange={(e) => updateCaption(e.target.value)}
                      rows={8}
                      className="min-h-40 rounded-xl text-sm"
                      placeholder={intl.formatMessage({
                        id: "campaignPost.captionPlaceholder",
                        defaultMessage: "Write a caption…",
                      })}
                    />
                    <div className="space-y-1.5">
                      <span className="text-[11px] font-medium text-foreground/50">
                        <FormattedMessage id="campaignPost.hashtags" defaultMessage="Hashtags" />
                      </span>
                      <div className="flex flex-wrap gap-1.5">
                        {getCurrentHashtags().map((token, index) => (
                          <span
                            key={`${token}-${index}`}
                            className="inline-flex items-center gap-1 rounded-full bg-muted/60 px-2.5 py-1 text-xs text-foreground/70"
                          >
                            <span>#{token}</span>
                            <button
                              type="button"
                              className="text-foreground/35 hover:text-destructive"
                              onClick={() =>
                                updateHashtags(getCurrentHashtags().filter((_, i) => i !== index))
                              }
                            >
                              <X className="h-3 w-3" aria-hidden="true" />
                            </button>
                          </span>
                        ))}
                      </div>
                      <CaptionHashtagInput
                        hashtags={getCurrentHashtags()}
                        onChange={updateHashtags}
                      />
                    </div>
                  </div>
                ) : selectedBlock ? (
                  <>
                    {/* Layer actions bar */}
                    <div className="mb-4 flex items-center gap-1 rounded-2xl border border-border/70 bg-background/75 p-1 shadow-[inset_0_1px_0_rgba(255,255,255,0.8)]">
                      <button
                        type="button"
                        title={intl.formatMessage({
                          id: "campaignPost.layerAction.bringToFront",
                          defaultMessage: "Bring to front",
                        })}
                        onClick={moveLayerToFront}
                        className="flex h-8 w-8 items-center justify-center rounded-xl text-foreground/50 transition-colors hover:bg-muted/70 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
                      >
                        <BringToFront className="h-3.5 w-3.5" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        title={intl.formatMessage({
                          id: "campaignPost.layerAction.moveForward",
                          defaultMessage: "Move forward",
                        })}
                        onClick={moveLayerForward}
                        className="flex h-8 w-8 items-center justify-center rounded-xl text-foreground/50 transition-colors hover:bg-muted/70 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
                      >
                        <MoveUp className="h-3.5 w-3.5" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        title={intl.formatMessage({
                          id: "campaignPost.layerAction.moveBackward",
                          defaultMessage: "Move backward",
                        })}
                        onClick={moveLayerBackward}
                        className="flex h-8 w-8 items-center justify-center rounded-xl text-foreground/50 transition-colors hover:bg-muted/70 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
                      >
                        <MoveDown className="h-3.5 w-3.5" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        title={intl.formatMessage({
                          id: "campaignPost.layerAction.sendToBack",
                          defaultMessage: "Send to back",
                        })}
                        onClick={moveLayerToBack}
                        className="flex h-8 w-8 items-center justify-center rounded-xl text-foreground/50 transition-colors hover:bg-muted/70 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
                      >
                        <SendToBack className="h-3.5 w-3.5" aria-hidden="true" />
                      </button>
                      <div className="flex-1" />
                      <button
                        type="button"
                        title={selectedBlock.visible ? "Hide layer" : "Show layer"}
                        onClick={() =>
                          updateSelectedBlock("Toggle visibility", {
                            visible: !selectedBlock.visible,
                          })
                        }
                        className={[
                          "flex h-8 w-8 items-center justify-center rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50",
                          selectedBlock.visible
                            ? "text-foreground/50 hover:bg-muted/70 hover:text-foreground"
                            : "text-foreground/25 hover:bg-muted/70 hover:text-foreground/60",
                        ].join(" ")}
                      >
                        {selectedBlock.visible ? (
                          <Eye className="h-3.5 w-3.5" aria-hidden="true" />
                        ) : (
                          <EyeOff className="h-3.5 w-3.5" aria-hidden="true" />
                        )}
                      </button>
                      <button
                        type="button"
                        title={intl.formatMessage({
                          id: "campaignPost.layerAction.deleteLayer",
                          defaultMessage: "Delete layer",
                        })}
                        onClick={removeSelectedLayer}
                        disabled={
                          !canRemoveSelectedLayer(
                            activePlan?.scene.blocks ?? [],
                            selectedBlockId ?? "",
                          )
                        }
                        className="flex h-8 w-8 items-center justify-center rounded-xl text-foreground/35 transition-colors hover:bg-destructive/10 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-30"
                      >
                        <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                      </button>
                    </div>

                    {/* Block-specific sections */}
                    <div className="space-y-2">
                      {/* Text block */}
                      {selectedBlock.type === "text" ? (
                        <>
                          <InspectorSection
                            title={intl.formatMessage({
                              id: "campaignPost.inspector.typography",
                              defaultMessage: "Typography",
                            })}
                            defaultOpen={true}
                          >
                            <div className="space-y-2.5">
                              {/* Content */}
                              <Textarea
                                value={selectedBlock.text}
                                onChange={(event) =>
                                  updateSelectedBlock("Update text content", {
                                    text: event.target.value,
                                  })
                                }
                                rows={3}
                                className="min-h-[6.5rem] rounded-xl text-sm"
                                placeholder={intl.formatMessage({
                                  id: "campaignPost.inspector.textPlaceholder",
                                  defaultMessage: "Text content…",
                                })}
                              />

                              {/* Font family */}
                              <InspectorSelectField
                                label={intl.formatMessage({
                                  id: "campaignPost.inspector.fontFamily",
                                  defaultMessage: "Font family",
                                })}
                                value={selectedBlock.fontFamily}
                                options={availableFontFamilies}
                                previewOptions={true}
                                onChange={(value) =>
                                  updateSelectedBlock("Change font family", { fontFamily: value })
                                }
                              />

                              {/* Font size + line height */}
                              <div className="grid grid-cols-2 gap-2">
                                <InspectorNumberField
                                  label={intl.formatMessage({
                                    id: "campaignPost.inspector.fontSize",
                                    defaultMessage: "Size",
                                  })}
                                  value={selectedBlock.fontSize}
                                  min={1}
                                  onCommit={(value) =>
                                    updateSelectedBlock("Adjust font size", { fontSize: value })
                                  }
                                />
                                <InspectorNumberField
                                  label={intl.formatMessage({
                                    id: "campaignPost.inspector.lineHeight",
                                    defaultMessage: "Line height",
                                  })}
                                  value={selectedBlock.lineHeight}
                                  min={0.5}
                                  max={4}
                                  step={0.05}
                                  onCommit={(value) =>
                                    updateSelectedBlock("Adjust line height", { lineHeight: value })
                                  }
                                />
                              </div>

                              {/* Bold / Italic toggles */}
                              <div className="space-y-1.5">
                                <Label>
                                  <FormattedMessage
                                    id="campaignPost.inspector.style"
                                    defaultMessage="Style"
                                  />
                                </Label>
                                <div className="flex gap-1">
                                  {(
                                    [
                                      "normal",
                                      "bold",
                                      "italic",
                                      "bold italic",
                                    ] as SocialPostFontStyle[]
                                  ).map((style) => {
                                    const active = selectedBlock.fontStyle === style;
                                    const label =
                                      style === "normal"
                                        ? "Regular"
                                        : style === "bold"
                                          ? "Bold"
                                          : style === "italic"
                                            ? "Italic"
                                            : "Bold Italic";
                                    return (
                                      <button
                                        key={style}
                                        type="button"
                                        title={label}
                                        aria-pressed={active}
                                        onClick={() =>
                                          updateSelectedBlock("Change font style", {
                                            fontStyle: style,
                                          })
                                        }
                                        className={[
                                          "flex h-8 min-w-[2rem] flex-1 items-center justify-center rounded-lg border px-1.5 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50",
                                          active
                                            ? "border-accent/40 bg-accent/12 text-foreground"
                                            : "border-border/60 bg-background text-foreground/55 hover:bg-muted/50",
                                        ].join(" ")}
                                      >
                                        {style === "bold" || style === "bold italic" ? (
                                          <Bold className="h-3 w-3" aria-hidden="true" />
                                        ) : null}
                                        {style === "italic" || style === "bold italic" ? (
                                          <Italic className="h-3 w-3" aria-hidden="true" />
                                        ) : null}
                                        {style === "normal" ? (
                                          <span className="font-normal" aria-hidden="true">
                                            Aa
                                          </span>
                                        ) : null}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>

                              {/* Text align */}
                              <div className="space-y-1.5">
                                <Label>
                                  <FormattedMessage
                                    id="campaignPost.inspector.align"
                                    defaultMessage="Align"
                                  />
                                </Label>
                                <div className="flex gap-1">
                                  {(
                                    [
                                      { value: "left", icon: AlignLeft, label: "Left" },
                                      { value: "center", icon: AlignCenter, label: "Center" },
                                      { value: "right", icon: AlignRight, label: "Right" },
                                      { value: "justify", icon: AlignJustify, label: "Justify" },
                                    ] as {
                                      value: SocialPostTextAlign;
                                      icon: LucideIcon;
                                      label: string;
                                    }[]
                                  ).map(({ value, icon: Icon, label }) => {
                                    const active = selectedBlock.align === value;
                                    return (
                                      <button
                                        key={value}
                                        type="button"
                                        title={label}
                                        aria-pressed={active}
                                        onClick={() =>
                                          updateSelectedBlock("Change text align", { align: value })
                                        }
                                        className={[
                                          "flex h-8 flex-1 items-center justify-center rounded-lg border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50",
                                          active
                                            ? "border-accent/40 bg-accent/12 text-foreground"
                                            : "border-border/60 bg-background text-foreground/55 hover:bg-muted/50",
                                        ].join(" ")}
                                      >
                                        <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>

                              {/* Vertical align */}
                              <div className="space-y-1.5">
                                <Label>
                                  <FormattedMessage
                                    id="campaignPost.inspector.verticalAlign"
                                    defaultMessage="Vertical align"
                                  />
                                </Label>
                                <div className="flex gap-1">
                                  {(
                                    [
                                      { value: "top", icon: AlignStartVertical, label: "Top" },
                                      {
                                        value: "middle",
                                        icon: AlignCenterVertical,
                                        label: "Middle",
                                      },
                                      { value: "bottom", icon: AlignEndVertical, label: "Bottom" },
                                    ] as {
                                      value: SocialPostVerticalAlign;
                                      icon: LucideIcon;
                                      label: string;
                                    }[]
                                  ).map(({ value, icon: Icon, label }) => {
                                    const active = selectedBlock.verticalAlign === value;
                                    return (
                                      <button
                                        key={value}
                                        type="button"
                                        title={label}
                                        aria-pressed={active}
                                        onClick={() =>
                                          updateSelectedBlock("Change vertical align", {
                                            verticalAlign: value,
                                          })
                                        }
                                        className={[
                                          "flex h-8 flex-1 items-center justify-center rounded-lg border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50",
                                          active
                                            ? "border-accent/40 bg-accent/12 text-foreground"
                                            : "border-border/60 bg-background text-foreground/55 hover:bg-muted/50",
                                        ].join(" ")}
                                      >
                                        <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>

                              {/* Letter spacing */}
                              <InspectorSliderField
                                label={intl.formatMessage({
                                  id: "campaignPost.inspector.letterSpacing",
                                  defaultMessage: "Letter spacing",
                                })}
                                value={selectedBlock.letterSpacing}
                                min={-10}
                                max={40}
                                step={0.5}
                                valueLabel={`${selectedBlock.letterSpacing}`}
                                onCommit={(value) =>
                                  updateSelectedBlock("Adjust letter spacing", {
                                    letterSpacing: value,
                                  })
                                }
                              />
                            </div>
                          </InspectorSection>

                          {/* Appearance */}
                          <InspectorSection
                            title={intl.formatMessage({
                              id: "campaignPost.inspector.appearance",
                              defaultMessage: "Appearance",
                            })}
                            defaultOpen={true}
                          >
                            <div className="space-y-2.5">
                              <InspectorColorField
                                label={intl.formatMessage({
                                  id: "campaignPost.inspector.textColor",
                                  defaultMessage: "Color",
                                })}
                                value={selectedBlock.fill}
                                brandColors={brandPrimaryColors}
                                onCommit={(value) =>
                                  updateSelectedBlock("Update text color", { fill: value })
                                }
                              />
                              <div className="space-y-1.5">
                                <div className="flex items-center justify-between gap-2">
                                  <Label>
                                    <FormattedMessage
                                      id="campaignPost.inspector.stroke"
                                      defaultMessage="Stroke"
                                    />
                                  </Label>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      updateSelectedBlock("Toggle text stroke", {
                                        stroke:
                                          selectedBlock.stroke !== undefined
                                            ? undefined
                                            : "#171717",
                                        strokeWidth:
                                          selectedBlock.stroke !== undefined
                                            ? 0
                                            : Math.max(selectedBlock.strokeWidth ?? 0, 2),
                                      })
                                    }
                                    className={[
                                      "h-5 w-9 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50",
                                      selectedBlock.stroke !== undefined
                                        ? "bg-foreground/70"
                                        : "bg-foreground/15",
                                    ].join(" ")}
                                    aria-label={intl.formatMessage({
                                      id: "campaignPost.inspector.toggleTextStroke",
                                      defaultMessage: "Toggle text stroke",
                                    })}
                                    aria-pressed={selectedBlock.stroke !== undefined}
                                  >
                                    <span
                                      className={[
                                        "block h-4 w-4 rounded-full bg-white shadow transition-transform",
                                        selectedBlock.stroke !== undefined
                                          ? "translate-x-4"
                                          : "translate-x-0.5",
                                      ].join(" ")}
                                    />
                                  </button>
                                </div>
                                {selectedBlock.stroke !== undefined ? (
                                  <>
                                    <InspectorColorField
                                      label={intl.formatMessage({
                                        id: "campaignPost.inspector.stroke",
                                        defaultMessage: "Stroke",
                                      })}
                                      value={selectedBlock.stroke}
                                      brandColors={brandPrimaryColors}
                                      onCommit={(value) =>
                                        updateSelectedBlock("Update text stroke color", {
                                          stroke: value || "#171717",
                                        })
                                      }
                                    />
                                    <InspectorSliderField
                                      label={intl.formatMessage({
                                        id: "campaignPost.inspector.width",
                                        defaultMessage: "Width",
                                      })}
                                      value={selectedBlock.strokeWidth ?? 0}
                                      min={0}
                                      max={24}
                                      step={1}
                                      valueLabel={`${selectedBlock.strokeWidth ?? 0}px`}
                                      onCommit={(value) =>
                                        updateSelectedBlock("Adjust text stroke width", {
                                          strokeWidth: value,
                                        })
                                      }
                                    />
                                  </>
                                ) : null}
                              </div>
                              <InspectorSliderField
                                label={intl.formatMessage({
                                  id: "campaignPost.inspector.opacity",
                                  defaultMessage: "Opacity",
                                })}
                                value={selectedBlock.opacity}
                                min={0}
                                max={1}
                                step={0.01}
                                valueLabel={`${Math.round(selectedBlock.opacity * 100)}%`}
                                onCommit={(value) =>
                                  updateSelectedBlock("Adjust opacity", { opacity: value })
                                }
                              />
                            </div>
                          </InspectorSection>
                        </>
                      ) : null}

                      {/* Image / Logo block */}
                      {selectedBlock.type === "image" || selectedBlock.type === "logo" ? (
                        <InspectorSection
                          title={intl.formatMessage({
                            id: "campaignPost.inspector.image",
                            defaultMessage: "Image",
                          })}
                          defaultOpen={true}
                        >
                          <div className="space-y-3">
                            {selectedBlock.type === "logo" ? (
                              <CreativeLogoPicker
                                logos={logoOptions}
                                selectedObjectKey={selectedBlock.assetObjectKey ?? null}
                                disabled={!isPostEditable}
                                uploading={
                                  createLogoUpload.isPending ||
                                  completeLogoUpload.isPending ||
                                  deleteLogo.isPending
                                }
                                onSelectLogo={(logo) =>
                                  updateSelectedBlock("Change logo", {
                                    assetObjectKey: logo.objectKey,
                                  })
                                }
                                onDeleteLogo={deleteLogoFromPicker}
                                onUploadLogo={uploadLogoForSelectedBlock}
                              />
                            ) : null}
                            <InspectorFitField
                              value={selectedBlock.fit}
                              onChange={(value) =>
                                updateSelectedBlock("Update image fit", { fit: value })
                              }
                            />
                            <InspectorCornerRadiusField
                              value={selectedBlock.cornerRadius}
                              maxRoundRadius={Math.round(
                                Math.min(selectedBlock.width, selectedBlock.height) / 2,
                              )}
                              onChange={(value) =>
                                updateSelectedBlock("Adjust corner radius", { cornerRadius: value })
                              }
                            />
                            <InspectorSliderField
                              label={intl.formatMessage({
                                id: "campaignPost.inspector.opacity",
                                defaultMessage: "Opacity",
                              })}
                              value={selectedBlock.opacity}
                              min={0}
                              max={1}
                              step={0.01}
                              valueLabel={`${Math.round(selectedBlock.opacity * 100)}%`}
                              onCommit={(value) =>
                                updateSelectedBlock("Adjust opacity", { opacity: value })
                              }
                            />
                          </div>
                        </InspectorSection>
                      ) : null}

                      {/* Shape / Frame block */}
                      {selectedBlock.type === "rect" || selectedBlock.type === "frame" ? (
                        <InspectorSection
                          title={intl.formatMessage({
                            id: "campaignPost.inspector.shape",
                            defaultMessage: "Shape",
                          })}
                          defaultOpen={true}
                        >
                          <div className="space-y-3">
                            {"fill" in selectedBlock ? (
                              <InspectorColorField
                                label={intl.formatMessage({
                                  id: "campaignPost.inspector.fill",
                                  defaultMessage: "Fill",
                                })}
                                value={selectedBlock.fill ?? "#ffffff"}
                                allowClear={selectedBlock.type === "frame"}
                                brandColors={brandPrimaryColors}
                                onCommit={(value) =>
                                  updateSelectedBlock("Update fill color", {
                                    fill: value || undefined,
                                  })
                                }
                              />
                            ) : null}
                            <InspectorCornerRadiusField
                              value={selectedBlock.cornerRadius}
                              maxRoundRadius={Math.round(
                                Math.min(selectedBlock.width, selectedBlock.height) / 2,
                              )}
                              onChange={(value) =>
                                updateSelectedBlock("Adjust corner radius", { cornerRadius: value })
                              }
                            />
                            <InspectorSliderField
                              label={intl.formatMessage({
                                id: "campaignPost.inspector.opacity",
                                defaultMessage: "Opacity",
                              })}
                              value={selectedBlock.opacity}
                              min={0}
                              max={1}
                              step={0.01}
                              valueLabel={`${Math.round(selectedBlock.opacity * 100)}%`}
                              onCommit={(value) =>
                                updateSelectedBlock("Adjust opacity", { opacity: value })
                              }
                            />
                          </div>
                        </InspectorSection>
                      ) : null}

                      {/* Shape block */}
                      {selectedBlock.type === "shape" ? (
                        <InspectorSection
                          title={intl.formatMessage({
                            id: "campaignPost.inspector.shape",
                            defaultMessage: "Shape",
                          })}
                          defaultOpen={true}
                        >
                          <div className="space-y-2.5">
                            {/* Shape type picker */}
                            <div className="space-y-1.5">
                              <Label>
                                <FormattedMessage
                                  id="campaignPost.inspector.shapeType"
                                  defaultMessage="Type"
                                />
                              </Label>
                              <div className="grid grid-cols-2 gap-1">
                                {(["ellipse", "line"] as SocialPostShapeType[]).map((type) => {
                                  const active = selectedBlock.shapeType === type;
                                  return (
                                    <button
                                      key={type}
                                      type="button"
                                      aria-pressed={active}
                                      onClick={() => {
                                        const next = applyCreativeShapePreset(selectedBlock, type);
                                        updateSelectedBlock("Change shape type", {
                                          shapeType: next.shapeType,
                                          points: next.points,
                                          closed: next.closed,
                                          tension: next.tension,
                                          sides: next.sides,
                                          radius: next.radius,
                                          innerRadius: next.innerRadius,
                                          outerRadius: next.outerRadius,
                                          angle: next.angle,
                                          clockwise: next.clockwise,
                                          pathData: next.pathData,
                                          fill: next.fill,
                                          stroke: next.stroke,
                                          strokeWidth: next.strokeWidth,
                                          lineCap: next.lineCap,
                                          lineJoin: next.lineJoin,
                                        });
                                      }}
                                      className={[
                                        "flex h-8 items-center justify-center rounded-lg border text-xs font-medium capitalize transition-colors",
                                        active
                                          ? "border-accent/40 bg-accent/12 text-foreground"
                                          : "border-border/60 bg-background text-foreground/55 hover:bg-muted/50",
                                      ].join(" ")}
                                    >
                                      {type}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                            {/* Fill — not applicable to lines */}
                            {selectedBlock.shapeType !== "line" ? (
                              <div className="space-y-1.5">
                                <div className="flex items-center justify-between gap-2">
                                  <Label>
                                    <FormattedMessage
                                      id="campaignPost.inspector.fill"
                                      defaultMessage="Fill"
                                    />
                                  </Label>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      updateSelectedBlock("Toggle fill", {
                                        fill:
                                          selectedBlock.fill !== undefined ? undefined : "#171717",
                                      })
                                    }
                                    className={[
                                      "h-5 w-9 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50",
                                      selectedBlock.fill !== undefined
                                        ? "bg-foreground/70"
                                        : "bg-foreground/15",
                                    ].join(" ")}
                                    aria-pressed={selectedBlock.fill !== undefined}
                                  >
                                    <span
                                      className={[
                                        "block h-4 w-4 rounded-full bg-white shadow transition-transform",
                                        selectedBlock.fill !== undefined
                                          ? "translate-x-4"
                                          : "translate-x-0.5",
                                      ].join(" ")}
                                    />
                                  </button>
                                </div>
                                {selectedBlock.fill !== undefined ? (
                                  <InspectorColorField
                                    label=""
                                    value={selectedBlock.fill}
                                    brandColors={brandPrimaryColors}
                                    onCommit={(value) =>
                                      updateSelectedBlock("Update fill color", {
                                        fill: value || "#171717",
                                      })
                                    }
                                  />
                                ) : null}
                              </div>
                            ) : null}
                            {/* Stroke */}
                            <div className="space-y-1.5">
                              <div className="flex items-center justify-between gap-2">
                                <Label>
                                  <FormattedMessage
                                    id="campaignPost.inspector.stroke"
                                    defaultMessage="Stroke"
                                  />
                                </Label>
                                <button
                                  type="button"
                                  onClick={() =>
                                    updateSelectedBlock("Toggle stroke", {
                                      stroke:
                                        selectedBlock.stroke !== undefined ? undefined : "#171717",
                                      strokeWidth:
                                        selectedBlock.stroke !== undefined
                                          ? 0
                                          : Math.max(selectedBlock.strokeWidth, 2),
                                    })
                                  }
                                  className={[
                                    "h-5 w-9 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50",
                                    selectedBlock.stroke !== undefined
                                      ? "bg-foreground/70"
                                      : "bg-foreground/15",
                                  ].join(" ")}
                                  aria-pressed={selectedBlock.stroke !== undefined}
                                >
                                  <span
                                    className={[
                                      "block h-4 w-4 rounded-full bg-white shadow transition-transform",
                                      selectedBlock.stroke !== undefined
                                        ? "translate-x-4"
                                        : "translate-x-0.5",
                                    ].join(" ")}
                                  />
                                </button>
                              </div>
                              {selectedBlock.stroke !== undefined ? (
                                <>
                                  <InspectorColorField
                                    label=""
                                    value={selectedBlock.stroke}
                                    brandColors={brandPrimaryColors}
                                    onCommit={(value) =>
                                      updateSelectedBlock("Update stroke color", {
                                        stroke: value || "#171717",
                                      })
                                    }
                                  />
                                  <InspectorSliderField
                                    label={intl.formatMessage({
                                      id: "campaignPost.inspector.width",
                                      defaultMessage: "Width",
                                    })}
                                    value={selectedBlock.strokeWidth}
                                    min={0}
                                    max={24}
                                    step={1}
                                    valueLabel={`${selectedBlock.strokeWidth}px`}
                                    onCommit={(value) =>
                                      updateSelectedBlock("Adjust stroke width", {
                                        strokeWidth: value,
                                      })
                                    }
                                  />
                                </>
                              ) : null}
                            </div>
                            <InspectorSliderField
                              label={intl.formatMessage({
                                id: "campaignPost.inspector.opacity",
                                defaultMessage: "Opacity",
                              })}
                              value={selectedBlock.opacity}
                              min={0}
                              max={1}
                              step={0.01}
                              valueLabel={`${Math.round(selectedBlock.opacity * 100)}%`}
                              onCommit={(value) =>
                                updateSelectedBlock("Adjust opacity", { opacity: value })
                              }
                            />
                          </div>
                        </InspectorSection>
                      ) : null}

                      {/* Opacity for non-text blocks that have no dedicated section for it */}
                      {selectedBlock.type !== "text" &&
                      selectedBlock.type !== "image" &&
                      selectedBlock.type !== "logo" &&
                      selectedBlock.type !== "rect" &&
                      selectedBlock.type !== "frame" &&
                      selectedBlock.type !== "shape" ? (
                        <InspectorSection
                          title={intl.formatMessage({
                            id: "campaignPost.inspector.appearance",
                            defaultMessage: "Appearance",
                          })}
                          defaultOpen={true}
                        >
                          <InspectorSliderField
                            label={intl.formatMessage({
                              id: "campaignPost.inspector.opacity",
                              defaultMessage: "Opacity",
                            })}
                            value={selectedBlock.opacity}
                            min={0}
                            max={1}
                            step={0.01}
                            valueLabel={`${Math.round(selectedBlock.opacity * 100)}%`}
                            onCommit={(value) =>
                              updateSelectedBlock("Adjust opacity", { opacity: value })
                            }
                          />
                        </InspectorSection>
                      ) : null}

                      {/* Transform — always shown */}
                      <InspectorSection
                        title={intl.formatMessage({
                          id: "campaignPost.inspector.transform",
                          defaultMessage: "Transform",
                        })}
                        defaultOpen={true}
                      >
                        <div className="space-y-2">
                          {selectedBlock.type === "rect" ? (
                            <RectangleSizePresetControl
                              width={selectedBlock.width}
                              height={selectedBlock.height}
                              onUpdate={(changes) =>
                                updateSelectedBlock("Update rectangle size", changes)
                              }
                              squareLabel={intl.formatMessage({
                                id: "campaignPost.inspector.squarePreset",
                                defaultMessage: "Square",
                              })}
                              rectangleLabel={intl.formatMessage({
                                id: "campaignPost.inspector.rectanglePreset",
                                defaultMessage: "Rectangle",
                              })}
                            />
                          ) : null}
                          <InspectorSliderField
                            label={intl.formatMessage({
                              id: "campaignPost.inspector.rotation",
                              defaultMessage: "Rotation",
                            })}
                            value={selectedBlock.rotation}
                            min={-180}
                            max={180}
                            step={1}
                            valueLabel={`${Math.round(selectedBlock.rotation)}°`}
                            onCommit={(value) =>
                              updateSelectedBlock("Rotate selected layer", { rotation: value })
                            }
                          />
                          <InspectorAdvancedGeometry
                            block={selectedBlock}
                            onUpdate={(changes: Record<string, unknown>) =>
                              updateSelectedBlock("Update geometry", changes)
                            }
                          />
                        </div>
                      </InspectorSection>
                    </div>
                  </>
                ) : (
                  <div className="px-2 py-10 text-center text-sm text-foreground/35">
                    <FormattedMessage
                      id="campaignPost.selectLayerHint"
                      defaultMessage="Select a layer to edit its properties"
                    />
                  </div>
                )}
              </div>
            )}
          </>
        }
      />
      <RunConfirmationDialog
        open={Boolean(pendingImageRegenerationRequest)}
        title={intl.formatMessage({
          id: "campaignPost.imageRunConfirmTitle",
          defaultMessage: "Use runs to regenerate this image?",
        })}
        description={intl.formatMessage({
          id: "campaignPost.imageRunConfirmDescription",
          defaultMessage: "Cheetah will queue a new image generation for this campaign post.",
        })}
        confirmLabel={intl.formatMessage({
          id: "campaignPost.imageRunConfirmAction",
          defaultMessage: "Regenerate and use runs",
        })}
        quote={imageRegenerationQuoteQuery.data ?? null}
        quotePending={
          imageRegenerationQuoteQuery.isPending || imageRegenerationQuoteQuery.isFetching
        }
        confirmPending={regenerateImageMutation.isPending}
        error={
          imageRegenerationQuoteQuery.error?.message ??
          regenerateImageMutation.error?.message ??
          null
        }
        onCancel={cancelImageRegeneration}
        upgradeLabel={assetCreationGate.upgradeLabel}
        onUpgrade={assetCreationGate.openUpgradeModal}
        onConfirm={() => void confirmImageRegeneration()}
      />
      {assetCreationGate.upgradeModal}
    </>
  );
}

export default CampaignPostPage;

function hasBackgroundLocationState(state: unknown): boolean {
  return Boolean(state && typeof state === "object" && "backgroundLocation" in state);
}

function SortableLayerRow({
  entry,
  isActive,
  isTransformActive,
  onSelectBlock,
  onRenameBlock,
  onToggleVisibility,
}: {
  entry: CreativeLayerEntry;
  isActive: boolean;
  isTransformActive: boolean;
  onSelectBlock: (blockId: string, options?: { toggle?: boolean }) => void;
  onRenameBlock: (blockId: string, layerName: string) => void;
  onToggleVisibility: (blockId: string, visible: boolean) => void;
}) {
  const intl = useIntl();
  const block = entry.block;
  const layerLabel = getLayerLabel(block, intl);

  return (
    <CreativeLayerSortableRow
      entry={entry}
      isActive={isActive}
      isTransformActive={isTransformActive}
      layerLabel={layerLabel}
      editableLayerName={getEditableLayerName(block, intl)}
      renameAriaLabel={`Rename ${layerLabel}`}
      reorderAriaLabel={intl.formatMessage(
        {
          id: "campaignPost.reorderLayerAria",
          defaultMessage: "Reorder {label}",
        },
        { label: layerLabel },
      )}
      hideAriaLabel={intl.formatMessage(
        {
          id: "campaignPost.hideLayerAria",
          defaultMessage: "Hide {label}",
        },
        { label: layerLabel },
      )}
      showAriaLabel={intl.formatMessage(
        {
          id: "campaignPost.showLayerAria",
          defaultMessage: "Show {label}",
        },
        { label: layerLabel },
      )}
      onSelectBlock={onSelectBlock}
      onRenameBlock={onRenameBlock}
      onToggleVisibility={onToggleVisibility}
    />
  );
}

function getLayerLabel(block: SocialPostSceneBlockV1, intl: ReturnType<typeof useIntl>) {
  const customName = getCustomLayerName(block);
  if (customName && block.type !== "text") {
    return customName;
  }

  switch (block.type) {
    case "text":
      return truncateLayerLabel(block.text);
    case "image":
      return intl.formatMessage({
        id: "campaignPost.layer.image",
        defaultMessage: "Image",
      });
    case "logo":
      return intl.formatMessage({
        id: "campaignPost.layer.logo",
        defaultMessage: "Logo",
      });
    case "rect":
      return intl.formatMessage({
        id: "campaignPost.layer.shapeBackground",
        defaultMessage: "Shape background",
      });
    case "frame":
      return intl.formatMessage({
        id: "campaignPost.layer.frame",
        defaultMessage: "Frame",
      });
    case "shape":
      return intl.formatMessage(
        {
          id: "campaignPost.layer.shape",
          defaultMessage: "{shapeType} shape",
        },
        { shapeType: `${block.shapeType[0]?.toUpperCase() ?? "S"}${block.shapeType.slice(1)}` },
      );
    default:
      return intl.formatMessage({
        id: "campaignPost.layer.default",
        defaultMessage: "Layer",
      });
  }
}

function getEditableLayerName(block: SocialPostSceneBlockV1, intl: ReturnType<typeof useIntl>) {
  return getCustomLayerName(block) ?? getLayerLabel(block, intl);
}

function InspectorAdvancedGeometry({
  block,
  onUpdate,
}: {
  block: SocialPostSceneBlockV1;
  onUpdate: (changes: Record<string, unknown>) => void;
}) {
  const intl = useIntl();
  return (
    <CreativeInspectorAdvancedGeometry
      block={block}
      title={intl.formatMessage({
        id: "campaignPost.inspector.advancedGeometry",
        defaultMessage: "Advanced geometry",
      })}
      labels={{
        x: intl.formatMessage({ id: "campaignPost.inspector.x", defaultMessage: "X" }),
        y: intl.formatMessage({ id: "campaignPost.inspector.y", defaultMessage: "Y" }),
        width: intl.formatMessage({ id: "campaignPost.inspector.width", defaultMessage: "Width" }),
        height: intl.formatMessage({
          id: "campaignPost.inspector.height",
          defaultMessage: "Height",
        }),
      }}
      onUpdate={onUpdate}
    />
  );
}

function InspectorColorField({
  label,
  value,
  onCommit,
  allowClear = false,
  brandColors = [],
}: {
  label: string;
  value: string;
  onCommit: (value: string) => void;
  allowClear?: boolean;
  brandColors?: string[];
}) {
  const intl = useIntl();
  return (
    <CreativeInspectorColorField
      label={label}
      value={value}
      allowClear={allowClear}
      brandColors={brandColors}
      clearLabel={intl.formatMessage({
        id: "campaignPost.inspector.clear",
        defaultMessage: "Clear",
      })}
      colorPickerLabels={{
        brandPalette: intl.formatMessage({
          id: "campaignPost.inspector.brandPalette",
          defaultMessage: "Brand palette",
        }),
        hexInput: intl.formatMessage({
          id: "campaignPost.inspector.hexColor",
          defaultMessage: "Hex color",
        }),
        hueSlider: intl.formatMessage({ id: "campaignPost.inspector.hue", defaultMessage: "Hue" }),
        openPicker: intl.formatMessage({
          id: "campaignPost.inspector.openColorPicker",
          defaultMessage: "Open color picker",
        }),
        useBrandColor: (color) =>
          intl.formatMessage(
            {
              id: "campaignPost.inspector.useBrandColor",
              defaultMessage: "Use brand color {color}",
            },
            { color },
          ),
      }}
      onCommit={onCommit}
    />
  );
}

function InspectorFitField({
  value,
  onChange,
}: {
  value: ImageFitValue;
  onChange: (value: ImageFitValue) => void;
}) {
  const intl = useIntl();
  return (
    <CreativeInspectorFitField
      intl={intl}
      label={<FormattedMessage id="campaignPost.inspector.imageFit" defaultMessage="Image fit" />}
      value={value}
      onChange={onChange}
    />
  );
}

function InspectorCornerRadiusField({
  value,
  maxRoundRadius,
  onChange,
}: {
  value: number;
  maxRoundRadius: number;
  onChange: (value: number) => void;
}) {
  const intl = useIntl();
  return (
    <CreativeInspectorCornerRadiusField
      label={
        <FormattedMessage id="campaignPost.inspector.cornerRadius" defaultMessage="Corner radius" />
      }
      customLabel={intl.formatMessage({
        id: "campaignPost.inspector.cornerRadius.custom",
        defaultMessage: "Custom",
      })}
      optionLabels={{
        square: intl.formatMessage({
          id: "campaignPost.inspector.cornerRadius.square",
          defaultMessage: "Square",
        }),
        soft: intl.formatMessage({
          id: "campaignPost.inspector.cornerRadius.soft",
          defaultMessage: "Soft",
        }),
        round: intl.formatMessage({
          id: "campaignPost.inspector.cornerRadius.round",
          defaultMessage: "Round",
        }),
      }}
      value={value}
      maxRoundRadius={maxRoundRadius}
      onChange={onChange}
    />
  );
}

function CaptionHashtagInput({
  hashtags,
  onChange,
}: {
  hashtags: string[];
  onChange: (hashtags: string[]) => void;
}) {
  const intl = useIntl();
  return (
    <CreativePostHashtagInput
      hashtags={hashtags}
      placeholder={intl.formatMessage({
        id: "campaignPost.hashtagPlaceholder",
        defaultMessage: "#hashtag",
      })}
      addLabel={<FormattedMessage id="campaignPost.hashtagAdd" defaultMessage="Add" />}
      onChange={onChange}
    />
  );
}
